import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { QUERY_KEYS } from "./query-keys";
import { toast } from "sonner";
import { useUserSettings } from "@/lib/user-settings-context";
import { GoogleSheetSyncService } from "@/services/GoogleSheetSyncService";

export const useSessions = (userId?: string, filter?: string, limit?: number) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.sessions(userId), filter, limit],
    queryFn: async () => {
      if (!userId) return [];
      let query = supabase
        .from("sessions")
        .select("*, session_players(player_id, buy_ins, cash_out, net_amount, players(name))")
        .eq("created_by", userId)
        .order("date", { ascending: false });

      if (filter && filter !== 'all') {
        query = query.eq('status', filter);
      }

      if (limit) {
        query = query.limit(limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
  });
};

export const useSession = (sessionId?: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.session(sessionId),
    queryFn: async () => {
      if (!sessionId) return null;
      const { data, error } = await supabase
        .from("sessions")
        .select("*")
        .eq("id", sessionId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!sessionId,
  });
};

export const useCreateSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (session: any) => {
      const { data, error } = await supabase
        .from("sessions")
        .insert([session])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessions(data.created_by) });
      queryClient.invalidateQueries({ queryKey: ['session-players', 'all'] });
      toast.success("Session created successfully");
    },
    onError: (error: any) => {
      toast.error(`Error creating session: ${error.message}`);
    },
  });
};

export const useUpdateSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...session }: any) => {
      const { data, error } = await supabase
        .from("sessions")
        .update(session)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessions(data.created_by) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.session(data.id) });
      queryClient.invalidateQueries({ queryKey: ['session-players', 'all'] });
      toast.success("Session updated successfully");
    },
    onError: (error: any) => {
      toast.error(`Error updating session: ${error.message}`);
    },
  });
};

export const useDeleteSession = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, userId }: { id: string; userId: string }) => {
      const { error } = await supabase.from("sessions").delete().eq("id", id);
      if (error) throw error;
      return { id, userId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessions(data.userId) });
      queryClient.invalidateQueries({ queryKey: ['session-players', 'all'] });
      toast.success("Session deleted successfully");
    },
    onError: (error: any) => {
      toast.error(`Error deleting session: ${error.message}`);
    },
  });
};

export const useSessionTemplates = (userId?: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.sessionTemplates(userId),
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("session_templates")
        .select("*")
        .eq("created_by", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
  });
};

export const useCreateSessionTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (template: { name: string; player_ids: string[]; created_by: string }) => {
      const { data, error } = await supabase
        .from("session_templates")
        .insert([template])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessionTemplates(data.created_by) });
      toast.success("Template saved successfully");
    },
    onError: (error: any) => {
      toast.error(`Error saving template: ${error.message}`);
    },
  });
};

export const useDeleteSessionTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, userId }: { id: string; userId: string }) => {
      const { error } = await supabase.from("session_templates").delete().eq("id", id);
      if (error) throw error;
      return { id, userId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessionTemplates(data.userId) });
      toast.success("Template deleted successfully");
    },
    onError: (error: any) => {
      toast.error(`Error deleting template: ${error.message}`);
    },
  });
};

export const useRoastMutation = () => {
  return useMutation({
    mutationFn: async (players: { name: string, buyIns: number }[]) => {
      const { data, error } = await supabase.functions.invoke('roast-active-session', {
        body: { players },
      });
      if (error) throw error;
      return data?.roast as string || '';
    },
  });
};

export const useFinishSessionMutation = () => {
  const queryClient = useQueryClient();
  const { updateSettings } = useUserSettings();

  return useMutation({
    mutationFn: async ({ sessionId, results, notes }: { sessionId: string; results: any[]; notes?: string }) => {
      // 1. Update session status to unsettled (settlement happens separately in the Settlement page)
      const updateData: any = { status: 'unsettled' };
      if (notes !== undefined) {
        updateData.notes = notes;
      }

      const { error: sessionError } = await supabase
        .from('sessions')
        .update(updateData)
        .eq('id', sessionId);
      
      if (sessionError) throw sessionError;

      // 2. Update player results
      for (const res of results) {
        const { error } = await supabase
          .from('session_players')
          .update({
            buy_ins: res.buyIns,
            cash_out: res.cashOut,
            net_amount: res.net
          })
          .eq('session_id', sessionId)
          .eq('player_id', res.id);

        if (error) throw error;
      }

      // 3. Clear active session in settings
      await updateSettings({ active_session_id: null });

      // 4. Sync to Google Sheets
      await GoogleSheetSyncService.syncSession(sessionId);

      return { sessionId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      queryClient.invalidateQueries({ queryKey: ['session-players'] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.session(data.sessionId) });
    }
  });
};
